import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CurrencyDollar, Warning, Download, Copy } from "@phosphor-icons/react";

/**
 * Ready-to-Bill — FR-completed WOs in the last N days that likely need an
 * AppFolio invoice. Bridges the gap between ClickUp "WORK DONE" (field state)
 * and AF "Completed" (billed state).
 */
export default function AdminReadyToBill() {
  const [days, setDays] = useState(14);
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ lcv: 0, procall: 0, unassigned: 0, other: 0 });
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [vendorFilter, setVendorFilter] = useState("all"); // all | lcv | procall | unassigned | other

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/reports/ready-to-bill?days=${days}`);
      setRows(r.data.rows || []);
      setCounts(r.data.vendor_counts || { lcv: 0, procall: 0, unassigned: 0, other: 0 });
    } catch (e) {
      toast.error("Could not load report");
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [days]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((r) => {
      if (vendorFilter !== "all" && r.vendor_bucket !== vendorFilter) return false;
      if (!q) return true;
      return [r.wo, r.title, r.property_address, r.unit, r.vendor, r.notes_preview]
        .some((v) => (v || "").toLowerCase().includes(q));
    });
  }, [rows, query, vendorFilter]);

  const csv = () => {
    const header = ["WO#", "Address", "Unit", "Vendor", "Title", "Completed", "Notes"];
    const esc = (v) => `"${String(v || "").replace(/"/g, '""')}"`;
    const body = filtered.map((r) => [
      r.wo, r.property_address, r.unit, r.vendor, r.title,
      (r.completed_at || "").slice(0, 10), r.notes_preview,
    ].map(esc).join(","));
    const blob = new Blob([[header.join(","), ...body].join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ready-to-bill-${days}d-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Downloaded ${filtered.length} rows`);
  };

  const copyClipboard = async () => {
    const txt = filtered
      .map((r) => `WO#${r.wo} · ${r.property_address}${r.unit ? " · #" + r.unit : ""} · ${r.vendor || "—"} · ${(r.completed_at || "").slice(0, 10)}`)
      .join("\n");
    try {
      await navigator.clipboard.writeText(txt);
      toast.success("Copied to clipboard");
    } catch { toast.error("Copy failed"); }
  };

  return (
    <div className="p-4 max-w-5xl mx-auto space-y-4" data-testid="ready-to-bill-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] font-black uppercase tracking-widest text-emerald-700 flex items-center gap-1">
            <CurrencyDollar weight="fill" size={12}/> Bookkeeper Bridge
          </div>
          <h1 className="text-2xl font-black tracking-tight">Ready to Bill</h1>
          <p className="text-sm text-slate-600 mt-1 max-w-xl">
            FR-completed work orders in the last {days} days. Cross-check against AppFolio's
            "Work Done Unbilled" / "Ready to Bill" queue — anything here without an AF invoice
            is money left on the table.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="text-[10px] font-black uppercase tracking-widest text-slate-600">Window</label>
          <select value={days} onChange={(e) => setDays(Number(e.target.value))}
            data-testid="rtb-days-select"
            className="h-9 px-2 border-2 border-slate-300 rounded font-black text-sm">
            <option value={7}>7 days</option>
            <option value={14}>14 days</option>
            <option value={30}>30 days</option>
            <option value={60}>60 days</option>
            <option value={90}>90 days</option>
          </select>
          <button onClick={csv} data-testid="rtb-csv-btn"
            className="h-9 px-3 border-2 border-slate-900 bg-emerald-500 hover:bg-emerald-600 text-slate-900 font-black uppercase text-[10px] tracking-widest rounded inline-flex items-center gap-1">
            <Download weight="bold" size={12}/> CSV
          </button>
          <button onClick={copyClipboard} data-testid="rtb-copy-btn"
            className="h-9 px-3 border-2 border-slate-300 bg-white hover:bg-slate-50 text-slate-800 font-black uppercase text-[10px] tracking-widest rounded inline-flex items-center gap-1">
            <Copy weight="bold" size={12}/> Copy
          </button>
        </div>
      </div>

      <input list="known-addresses" autoComplete="off" value={query} onChange={(e) => setQuery(e.target.value)}
        data-testid="rtb-search"
        placeholder="Filter by WO#, address, vendor…"
        className="w-full h-11 px-3 border-2 border-slate-300 rounded-md font-medium"/>

      {/* Vendor filter chips */}
      <div className="flex flex-wrap items-center gap-2" data-testid="rtb-vendor-chips">
        {[
          { key: "all",        label: "All",        n: rows.length,     cls: "border-slate-900 bg-slate-900 text-white" },
          { key: "lcv",        label: "LCV",        n: counts.lcv,       cls: "border-amber-500 bg-amber-500 text-slate-900" },
          { key: "procall",    label: "ProCall",    n: counts.procall,   cls: "border-cyan-500 bg-cyan-500 text-slate-900" },
          { key: "unassigned", label: "Unassigned", n: counts.unassigned, cls: "border-red-500 bg-red-500 text-white" },
          { key: "other",      label: "Other",      n: counts.other,     cls: "border-slate-400 bg-slate-200 text-slate-800" },
        ].map((c) => {
          const active = vendorFilter === c.key;
          return (
            <button key={c.key} onClick={() => setVendorFilter(c.key)}
              data-testid={`rtb-chip-${c.key}`}
              className={`h-9 px-3 border-2 font-black uppercase tracking-widest text-[10px] rounded inline-flex items-center gap-1.5 transition-transform ${active ? c.cls + " shadow-[2px_2px_0px_#0f172a] -translate-y-0.5" : "border-slate-300 bg-white hover:bg-slate-50 text-slate-700"}`}>
              {c.label}
              <span className={`inline-flex items-center h-4 min-w-4 px-1 rounded-sm font-mono text-[10px] ${active ? "bg-black/20" : "bg-slate-100 text-slate-700"}`}>{c.n}</span>
            </button>
          );
        })}
      </div>

      <div className="text-sm text-slate-600">
        Showing <b data-testid="rtb-count">{filtered.length}</b> unique WO{filtered.length === 1 ? "" : "s"} · last {days} days
        {vendorFilter !== "all" && <span> · filter <b className="uppercase">{vendorFilter}</b></span>}
      </div>

      {loading ? (
        <div className="p-8 text-center text-slate-500">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="p-8 text-center text-slate-500 border-2 border-dashed border-slate-300 rounded">
          Nothing completed in the last {days} days. Widen the window or check ClickUp for "WORK DONE" statuses.
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((r) => (
            <Link key={r.id} to={`/admin/jobs/${r.id}`}
              data-testid={`rtb-row-${r.id}`}
              className="block p-3 border-2 border-slate-200 hover:border-emerald-500 hover:bg-emerald-50 bg-white rounded transition-colors">
              <div className="flex items-start gap-2">
                <span className="inline-flex items-center h-5 px-1.5 bg-[#0f172a] text-white text-[10px] font-black uppercase tracking-widest font-mono flex-shrink-0">
                  WO#{r.wo || r.id.slice(-6)}
                </span>
                {r.dup_count > 1 && (
                  <span className="inline-flex items-center h-5 px-1.5 bg-fuchsia-100 border border-fuchsia-500 text-fuchsia-800 text-[10px] font-black uppercase tracking-widest" title={`Collapsed ${r.dup_count} duplicate rows`}>
                    ×{r.dup_count} twins
                  </span>
                )}
                {r.life_safety && (
                  <span className="inline-flex items-center gap-0.5 h-5 px-1.5 bg-red-600 text-white text-[10px] font-black uppercase tracking-widest">
                    <Warning weight="fill" size={10}/> Safety
                  </span>
                )}
                <span className="inline-flex items-center h-5 px-1.5 bg-emerald-100 border border-emerald-500 text-emerald-800 text-[10px] font-black uppercase tracking-widest">
                  Awaiting AF Invoice
                </span>
                <span className="text-[10px] text-slate-500 ml-auto whitespace-nowrap">
                  {(r.completed_at || "").slice(0, 10)}
                </span>
              </div>
              <div className="mt-1.5 font-black text-sm text-slate-900">{r.title}</div>
              <div className="text-xs text-slate-600">
                {r.property_address}{r.unit ? ` · #${r.unit}` : ""}
              </div>
              {r.vendor && (
                <div className="text-[11px] text-slate-500 mt-0.5">Vendor · <b>{r.vendor}</b></div>
              )}
              {r.notes_preview && (
                <div className="mt-1.5 text-[11px] text-slate-600 italic line-clamp-2">
                  "{r.notes_preview}"
                </div>
              )}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
